import axios from "axios";
import React, { useContext, useState, useEffect } from "react";
import AsyncSelect from "react-select/async";
import UserContext from "../../contexts/UserContext";
import { useNavigate } from "react-router-dom";
import authenticate from "../../utils/Authenticate";
import displayError from "../../utils/ErrorHandler";

const AddSupplier = () => {
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    company: "",
    address: "",
    username: "",
    password: "",
  });

  const [selectedOptions, setSelectedOptions] = useState([]);
  const [notification, setNotification] = useState({ message: "", type: "" });

  const loadOptions = async (inputValue, callback) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/findItems`,
        {
          params: { q: inputValue },
        }
      );
      const options = response.data.map((record) => ({
        value: record._id,
        label: record.name,
      }));
      callback(options);
    } catch (error) {
      console.error("Error loading options:", error);
    }
  };

  const handleItemChange = (selected) => {
    setSelectedOptions(selected);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const submitData = async (e) => {
    e.preventDefault();
    const finalObject = {
      supplier: {
        username: formData.username,
        password: formData.password,
        name: formData.name,
        company: formData.company,
        address: formData.address,
        contactNo: formData.contactNo, // Add contactNo
        accountDetails: {
          // Add account details
          accountNo: formData.accountDetails?.accountNo || null,
          name: formData.accountDetails?.name || null,
          ifscCode: formData.accountDetails?.ifscCode || null,
        },
        role: "Supplier",
        items: selectedOptions,
      },
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URI}/api/supplier/create`,
        finalObject,
        { withCredentials: true }
      );
      setNotification({
        message: response.data.message || "Supplier registered successfully!",
        type: "success",
      });
      setFormData({
        name: "",
        company: "",
        address: "",
        username: "",
        password: "",
        contactNo: "", // New field
        accountDetails: {
          // New nested fields
          accountNo: "",
          name: "",
          ifscCode: "",
        },
      });
      setSelectedOptions([]);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (err) {
      const errorMessage = displayError(err, navigate, setUser);
      console.log(errorMessage);
      if (errorMessage) {
        setNotification({ message: errorMessage, type: "error" });
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }
  };

  const handleAccountChange = (e) => {
    setFormData({
      ...formData,
      accountDetails: {
        ...formData.accountDetails,
        [e.target.name]: e.target.value,
      },
    });
  };

  useEffect(() => {
    async function startup() {
      if (!user) {
        await authenticate(setUser, navigate);
      }
    }
    startup();
  }, [user, setUser, navigate]);

  const Notification = ({ message, type }) => (
    <div className={`notification ${type}`}>{message}</div>
  );

  const design = (
    <>
      {notification.message && (
        <div
          className={`m-2 mb-4 p-4 ${
            notification.type === "success"
              ? "bg-green-200 border-green-600 text-green-600"
              : "bg-red-200 border-red-600 text-red-600"
          }`}
        >
          {notification.message}
        </div>
      )}
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-4 lg:px-8">
        <form className="space-y-6" onSubmit={submitData}>
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Supplier Name
            </label>
            <div className="mt-2">
              <input
                id="name"
                name="name"
                type="text"
                placeholder="Supplier Name"
                autoComplete="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="company"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Company Name
            </label>
            <div className="mt-2">
              <input
                id="company"
                name="company"
                type="text"
                placeholder="Supplier company"
                autoComplete="company"
                value={formData.company}
                onChange={handleChange}
                required
                className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
          {/* Contact Number */}
          <div>
            <label htmlFor="contactNo" className="block text-sm font-medium leading-6 text-gray-900">
              Contact Number
            </label>
            <div className="mt-2">
              <input
                id="contactNo"
                name="contactNo"
                type="text"
                placeholder="Contact Number"
                autoComplete="contactNo"
                value={formData.contactNo}
                onChange={handleChange}
                className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="address"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Address
            </label>
            <div className="mt-2">
              <input
                id="address"
                name="address"
                type="text"
                placeholder="Supplier address"
                autoComplete="address"
                value={formData.address}
                onChange={handleChange}
                required
                className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="multi-select"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Select Items that Supplier deals in:
            </label>
            <AsyncSelect
              id="multi-select"
              isMulti
              loadOptions={loadOptions}
              onChange={handleItemChange}
              value={selectedOptions}
              placeholder="Search and select options..."
              className="rounded-full"
            />
          </div>

          

          {/* Account Details */}
          <label className="block text-md font-medium leading-6 text-gray-900">
            Account Details
          </label>
          <div className="flex flex-col space-y-4 px-4 py-2 bg-gray-50 rounded-lg">
            <div>
              <label htmlFor="accountNo" className="block text-sm font-medium leading-6 text-gray-900">
                Account Number
              </label>
              <div className="mt-2">
                <input
                  id="accountNo"
                  name="accountNo"
                  type="text"
                  placeholder="Account Number"
                  autoComplete="accountNo"
                  value={formData.accountDetails?.accountNo}
                  onChange={handleAccountChange}
                  className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                Account Holder Name
              </label>
              <div className="mt-2">
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Account Holder Name"
                  autoComplete="name"
                  value={formData.accountDetails?.name}
                  onChange={handleAccountChange}
                  className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div>
              <label htmlFor="ifscCode" className="block text-sm font-medium leading-6 text-gray-900">
                IFSC Code
              </label>
              <div className="mt-2">
                <input
                  id="ifscCode"
                  name="ifscCode"
                  type="text"
                  placeholder="IFSC Code"
                  autoComplete="ifscCode"
                  value={formData.accountDetails?.ifscCode}
                  onChange={handleAccountChange}
                  className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>
          </div>
          <label className="block text-md font-medium leading-6 text-gray-900">
            Supplier Credentials
          </label>
          <div className="flex min-h-full flex-1 flex-col justify-center px-1 py-0 lg:px-8">
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Username
              </label>
              <div className="mt-1">
                <input
                  id="username"
                  name="username"
                  type="username"
                  placeholder="Username"
                  autoComplete="username"
                  value={formData.username}
                  onChange={handleChange}
                  className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div className="mt-2">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium leading-6 text-gray-700"
                >
                  Password
                </label>
              </div>
              <div className="mt-2">
                <input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Password"
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>
          </div>

          

          <div>
            <button
              type="submit"
              className="flex w-full justify-center rounded-full text-white bg-fmb px-3 py-3 text-sm font-semibold leading-6 shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              SUBMIT
            </button>
          </div>
        </form>
      </div>
    </>
  );

  return design;
};

export default AddSupplier;
